// ActivityStreamCard — the shell every event stream in the app renders into.
//
// W52.1 D-5. Three streams, three implementations:
//
//   心願詳情      Card + History + count badge + 展開全部 / 收起
//   紀念日詳情    bare `<ul>` + 「載入更多」, no card, no count, no collapse
//   首頁          long `divide-y` list, no collapse
//
// The wish one was already right and is what the user pointed at. This is its
// **shell**, extracted — not a fourth design.
//
// ## What is shared, and what is emphatically not
//
// Shared: the card surface, the `History` title glyph, the count badge, the
// toggle, and the rule for when the toggle may appear.
//
// NOT shared: how rows are obtained. The wish timeline picks its own query
// `limit` (10 → 50, so expanding is a server round trip); the anniversary
// section pages through `useInfiniteQuery` and must keep 「載入更多」; the
// dashboard receives its rows as props and can only slice. Those are three
// different strategies and folding them together would break two of them.
//
// **Collapse is display density; pagination is "how much is left to fetch".**
// They are different questions and a list that conflates them still pushes the
// first screen's other content off the bottom.
//
// The caller therefore owns `expanded` state and passes the numbers; this
// component decides nothing about data.

import type { ReactNode } from "react";
import { History, RefreshCcw } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils/cn";

/**
 * How many rows a collapsed stream shows. 10 is not a fresh choice: it is the
 * value the wish timeline has shipped and had accepted since W35.2. Picking a
 * new number here would have meant re-deciding something already decided, and
 * the dashboard — the densest of the three, cross-module — is the one most
 * sensitive to it.
 */
export const ACTIVITY_COLLAPSED_LIMIT = 10;

export interface ActivityStreamCardProps {
  /** 繁體中文 heading, e.g. 「操作記錄」. */
  title: string;
  /**
   * The number in the badge — the stream's **total**, not the rows on screen.
   * Showing `total` while 10 are visible is what makes the toggle legible: the
   * reader can tell 12 from 3 without expanding anything.
   */
  total: number;
  /** How many rows the caller is actually rendering right now. */
  visibleCount: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** `data-testid` for the card root, so each site keeps its own handle. */
  testId: string;
  className?: string;
  /** `false` hides the control outright — e.g. mid-load, where the count is fiction. */
  collapsible?: boolean;
  children?: ReactNode;
}

/**
 * The toggle is derived here rather than passed in, so all three sites make
 * the same decision: **no control unless there is genuinely more to show.**
 * A control that appears over an empty list promises something behind it that
 * is not there, and one that never appears over a 40-row list makes the user
 * scroll to find the bottom.
 */
function hasMore(visibleCount: number): boolean {
  return visibleCount > ACTIVITY_COLLAPSED_LIMIT;
}

export function ActivityStreamCard({
  title,
  total,
  visibleCount,
  expanded,
  onToggleExpanded,
  testId,
  className,
  collapsible = true,
  children,
}: ActivityStreamCardProps) {
  const more = collapsible && hasMore(visibleCount);
  return (
    <Card data-testid={testId} className={cn("p-s-4", className)}>
      <header className="flex items-center justify-between gap-2 mb-s-3">
        <h3 className="font-display text-h3 text-ink-1 flex items-center gap-2">
          <History className="h-4 w-4" aria-hidden />
          {title}
          <span className="text-meta text-ink-3 font-normal">{total}</span>
        </h3>
        {more ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onToggleExpanded}
            aria-expanded={expanded}
            data-testid={`${testId}-toggle`}
          >
            <RefreshCcw className="h-3.5 w-3.5" aria-hidden />
            {expanded ? "收起" : "展開全部"}
          </Button>
        ) : null}
      </header>
      {children}
    </Card>
  );
}